import React, { useCallback, useEffect } from 'react'

import PropTypes from 'prop-types'

import {
  Button,
  Dialog,
  Fade,
  LinearProgress,
  Typography,
} from '@material-ui/core'
import { observer } from 'mobx-react'
import { useSnackbar } from 'notistack'
import { Link as RouterLink } from 'react-router-dom'

import { Alert } from 'shared/components/molecules'
import { MARKETPLACE_CODE_PRINT_ON_DEMAND } from 'shared/constants/marketplace'

import { useStores } from 'retailer/stores/hooks'

import { adaptKornitSaveStateCallbackPayload } from './adapters'
import style from './ItemCustomizerModal.module.scss'

const ItemCustomizerModal = ({ onExited }) => {
  const { itemCustomizerStore } = useStores()
  const {
    iframeOrigin,
    mei,
    iframeUrl,
    hide,
    setIsLoaded,
    setIsSaving,
    saveDraft,
  } = itemCustomizerStore

  const { enqueueSnackbar } = useSnackbar()

  /**
   * Runs when the draft was successfully saved.
   */
  const handleOnExited = () => {
    itemCustomizerStore.reset()
    onExited && onExited()
  }

  const handleEvent = useCallback(e => {
    if (e.origin === iframeOrigin && e.data.id === mei) {
      switch (e.data.name) {
        case 'RENDERER_READY':
          setIsLoaded(true)
          break
        case 'ADD_TO_CART_CALLBACK':
          break
        case 'SHOW_BUSY_NOW':
          setIsSaving(true)
          break
        case 'SAVE_STATE_CALLBACK':
          saveDraft(adaptKornitSaveStateCallbackPayload(e.data.body))
            .then(({ status, itemUuid }) => {
              const isDraft = status === 'draft'
              const actionPath = isDraft
                ? `/marketplace/${MARKETPLACE_CODE_PRINT_ON_DEMAND}/${itemUuid}/drafts`
                : '/my-products/import-list'
              const actionLabel = isDraft ? 'Go to product page' : 'Go to Import List'
              enqueueSnackbar(
                (isDraft
                  ? 'Your project has been successfully saved as a draft.'
                  : 'Your customized product has been successfully saved.'),
                {
                  key: 'pod-design-saved',
                  variant: 'success',
                  autoHideDuration: 3000,
                  action: key => (
                    <Button variant="outlined" component={RouterLink} to={actionPath}>
                      {actionLabel}
                    </Button>
                  )
                }
              )
            })
          break
        case '_CLOSE':
          hide()
          break
      }
    }
  }, [iframeOrigin, mei, setIsLoaded, setIsSaving, hide, saveDraft])

  useEffect(() => {
    window.addEventListener('message', handleEvent)
    return () => {
      window.removeEventListener('message', handleEvent)
      itemCustomizerStore.reset()
    }
  }, [])

  const editorVisible = itemCustomizerStore.productId && !itemCustomizerStore.error
  const statusVisible = !itemCustomizerStore.isLoaded || itemCustomizerStore.isSaving || itemCustomizerStore.error

  return (
    <Dialog
      fullScreen
      onClose={() => itemCustomizerStore.hide()}
      TransitionProps={{ onExited: handleOnExited }}
      open={itemCustomizerStore.open}
      className={style.ItemCustomizerModal}
    >
      {editorVisible && (
        <iframe
          className={style.Editor}
          src={iframeUrl}
        />
      )}
      {statusVisible && (
        <Fade in={statusVisible}>
          <div className={style.Wrapper}>
            {(itemCustomizerStore.isSaving || !itemCustomizerStore.isLoaded) && (
              <LinearProgress variant="indeterminate" className={style.Loader} />
            )}
            {itemCustomizerStore.error && (
              <Alert title="Something went wrong" severity="error" className={style.Alert}>
                <Typography>{itemCustomizerStore.error}</Typography>
              </Alert>
            )}
          </div>
        </Fade>
      )}
    </Dialog>
  )
}

ItemCustomizerModal.propTypes = {
  onExited: PropTypes.func
}

export default observer(ItemCustomizerModal)
